import { useMetadataBuilderStore } from "../../stores/metadata-builder-store";
import { useTranslation } from "react-i18next";
import { DateClassSchema, type ArtMetadata } from "../../lib/schedule-presets/art-metadata";
import type { MetadataDay } from "../../lib/metadata-history/metadata-targets";
import { formatArtValue } from "../../lib/schedule-art/format-art-value";
import { builderSampleWeek } from "../../lib/metadata-builder/sample-schedule";
import { weekdayKeys } from "../../lib/schedule-time/schedule-dates";
import { setDayDateFormat } from "../../lib/metadata-builder/one-slot-layout";
import { dispatchBuilderMetadata } from "../../lib/metadata-builder/edit-text";
import { dispatchMetadataEvent, endMetadataMerge } from "../../lib/metadata-history/dispatch";
import { MetadataField } from "../editor-panel/developer-section/MetadataField";
import { MetadataElementLayout } from "../editor-panel/developer-section/MetadataElementLayout";
import { metadataLabels } from "../editor-panel/developer-section/metadata-form-schema";
import { NativeSelect } from "../ui/native-select";
import { TextStyleControls } from "./TextStyleControls";
import { dayDateOffset, dayDateMetadataOffset, rotateSharedDayDate, useBuilderDateAnchors } from "../../lib/metadata-builder/day-date-layout";

const dateFormats = ["%d", "%m/%d", "%b %d", "%a", "%a %d"];

type DayDateEditorProps = { metadata: ArtMetadata; day: MetadataDay };

export function DayDateEditor({ metadata, day }: DayDateEditorProps) {
  const { t } = useTranslation();
  const sampleDate = useMetadataBuilderStore(state => state.sampleDate);
  const week = builderSampleWeek(sampleDate);
  const labels = metadataLabels(t);
  const date = metadata.days[day].date;
  const anchors = useBuilderDateAnchors();
  const offset = anchors ? dayDateOffset(metadata, anchors, day) : date.offset;
  const pattern = metadata.formats[date.format] ?? "%d";
  const sample = week[weekdayKeys.indexOf(day)]!;
  const path = `days.${day}.date`;
  return (
    <div className="space-y-3" onBlurCapture={endMetadataMerge}>
      <p className="text-neutral-500">{t("builder.stream.dayDateHelp")}</p>
      <label className="grid gap-1 text-xs font-medium">
        {t("metadata.field.format")}
        <NativeSelect value={pattern} className="h-8 py-1 text-xs" onChange={event => {
          endMetadataMerge();
          dispatchBuilderMetadata(setDayDateFormat(metadata, event.target.value));
        }}>
          {!dateFormats.includes(pattern) && <option value={pattern} disabled>{t("builder.dates.customFormat")}</option>}
          {dateFormats.map(pattern => <option key={pattern} value={pattern}>{formatArtValue(sample, pattern)}</option>)}
        </NativeSelect>
      </label>
      <MetadataElementLayout fields={[
        { key: "offset", content: <MetadataField schema={DateClassSchema.shape.offset} value={offset}
          label={labels.offset!} labels={labels} stacked onChange={value => {
            const local = DateClassSchema.shape.offset.parse(value);
            const point = anchors ? dayDateMetadataOffset(metadata, anchors, day, local) : local;
            dispatchMetadataEvent({ type: "position/set", path, point }, `${path}:offset`);
          }} /> },
        { key: "rotation", content: <MetadataField schema={DateClassSchema.shape.rotation} value={date.rotation}
          label={labels.rotation!} labels={labels} stacked onChange={value => {
            const rotation = DateClassSchema.shape.rotation.parse(value) ?? 0;
            dispatchBuilderMetadata(rotateSharedDayDate(metadata, rotation), `${path}:rotation`);
          }} /> },
      ]} />
      <TextStyleControls key={date.style} styleName={date.style} />
    </div>
  );
}
